import React, { useState } from 'react';
import { Check, Copy, Mail } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import type { WebhookProviderConfigViewProps } from '../types';
import { WebhookConfigCard } from '../shared/webhook-config-card';
import { readInboundEmailAddress } from '../webhook-config-draft';

export const EmailWebhookConfigView: React.FC<
  WebhookProviderConfigViewProps
> = (props) => {
  const [copied, setCopied] = useState(false);

  // Storage mints the address on every write path that lands on the `email`
  // provider, so there is nothing to create by hand — it simply is not there
  // until the trigger has been saved once.
  const address = readInboundEmailAddress(props.draft.eventConfig);

  const handleCopy = async (value: string): Promise<void> => {
    if (typeof navigator !== 'undefined' && navigator.clipboard !== undefined) {
      await navigator.clipboard.writeText(value);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-4">
      <WebhookConfigCard
        icon={<Mail />}
        title="Email address"
        description={
          address === undefined
            ? 'Mail sent to the address runs the workflow. Anyone who knows it can trigger a run, so treat it like a secret.'
            : undefined
        }
      >
        {address === undefined ? (
          <>
            {/*
             * Holds the place the real address will occupy, so creating the
             * trigger reads as this same card with its blank filled in. The
             * label alone described something that wasn't on screen yet.
             */}
            <div className="rounded-md border border-dashed bg-background px-3 py-2 text-center font-mono text-xs text-muted-foreground/70">
              {props.mode === 'create'
                ? 'generated when you create this trigger'
                : 'generated when you save this trigger'}
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-2">
              <Input readOnly value={address} className="font-mono text-xs" />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => void handleCopy(address)}
                aria-label="Copy email address"
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <Text.Caption className="text-muted-foreground block">
              Anyone who knows this address can run the workflow, so treat it
              like a secret. Save the workflow to activate it.
            </Text.Caption>
          </>
        )}
      </WebhookConfigCard>
    </div>
  );
};
